import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import styled from 'styled-components';
import AdaptButton from 'components/base/Button';
import { currencyFormat, plus } from 'utils/number';
import { useEffect, useMemo, useState } from 'react';
import { SplitBillService } from 'services/split-bill-service';
import { get } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import {
  SplitBillTransferDialog,
  SplitBillTransferTitle,
  SplitBillTransferSubTitle,
} from './index.view';
import { useAsyncError } from 'utils/hooks';

const Button = styled(AdaptButton)`
  && {
    font-weight: 700;
    font-size: 18px;
    line-height: 23px;
  }
`;

const Head = styled(Box)`
  && {
    background: #dbe1e6;
    border-radius: 8px;
    padding: 10px;
  }
`;

const BigBox = styled(Box)`
  && {
    border: 1px solid #dbe1e6;
    border-radius: 8px;
  }
`;

interface IText {
  title: string;
  detail: string;
  tipTitle?: string;
  tipDetail?: string;
}

const Text = ({ title, detail, tipTitle, tipDetail }: IText) => {
  return (
    <Grid container style={{ padding: '10px' }}>
      <Grid xs={6} item>
        <Typography variant="body1" style={{ textAlign: 'left' }}>
          {title}
        </Typography>
      </Grid>
      <Grid xs={6} item>
        <Typography variant="body1" style={{ textAlign: 'right' }}>
          {detail}
        </Typography>
      </Grid>
      {tipTitle ? (
        <>
          <Grid xs={6} item style={{ color: '#636F7A' }}>
            <Typography variant="body2" style={{ textAlign: 'left', paddingLeft: '20px' }}>
              {tipTitle}
            </Typography>
          </Grid>
          <Grid xs={6} item>
            <Typography variant="body2" style={{ textAlign: 'right' }}>
              {tipDetail}
            </Typography>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

const splitBillService = new SplitBillService();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TransferModal = ({ state, transfer }: { state: any; transfer: any }) => {
  const { paymentInfo, tip_amount: tipAmount, tip_percentage: tipPercentage } = state;
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const throwError = useAsyncError();

  useEffect(() => {
    if (get(transfer, 'amount')) {
      setOpen(true);
    } else {
      handleClose();
    }
  }, [transfer]);

  const handleClose = () => {
    setOpen(false);
  };

  const totalAmount = useMemo(
    () => plus(paymentInfo.amount, transfer?.amount || 0),
    [paymentInfo.amount, transfer?.amount],
  );

  const onClick = () => {
    const data = {
      currentSplitPid: paymentInfo.pid,
      amountParticipant: totalAmount,
      menuId: [],
    };
    if (transfer?.payload) {
      const menuId = transfer?.payload.map((item: { id: string }) => item.id);
      data.menuId = menuId;
    }

    splitBillService.updateTransfer(transfer.token, data).catch(() => {
      throwError();
    });
  };

  return (
    <SplitBillTransferDialog open={open} dividers={false} handleClose={handleClose}>
      <Container>
        <SplitBillTransferTitle>{t('splitBillTransferTitle')}</SplitBillTransferTitle>
        <SplitBillTransferSubTitle>{t('splitBillTransferSubTitle')}</SplitBillTransferSubTitle>
        <Box style={{ margin: '20px' }}>
          <BigBox>
            <Head>
              <Typography variant="h6" style={{ textAlign: 'center' }}>
                {t('splitBillTransferBill')}
              </Typography>
              <Typography variant="h4" style={{ textAlign: 'center' }}>
                {currencyFormat(totalAmount)}
              </Typography>
            </Head>

            <Text
              title="Your first bill"
              detail={currencyFormat(paymentInfo.amount)}
              tipTitle={tipPercentage && `Tip ${tipPercentage}% (Included)`}
              tipDetail={tipAmount && currencyFormat(tipAmount)}
            />
            <Text title="Your Participant bill" detail={currencyFormat(transfer?.amount)} />
          </BigBox>

          <Typography
            style={{ color: '#007AFF', padding: '15px 0px 15px 0px', margin: '10px 0px' }}
          >
            {t('splitBillTransferDescription')}
          </Typography>

          <Button
            fullWidth
            paylo
            variant="contained"
            type="submit"
            loadingColor="#fff"
            onClick={() => onClick()}
          >
            {t('splitBillTransferAddBill')}
          </Button>
        </Box>
      </Container>
    </SplitBillTransferDialog>
  );
};

export default TransferModal;
